import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  BookOpen, 
  Download, 
  Play,
  ChevronDown,
  ChevronRight,
  Users,
  Calendar,
  FileText,
  ClipboardCheck,
  MessageCircle,
  Settings,
  FolderOpen,
  BarChart3,
  HelpCircle,
  CheckCircle,
  AlertTriangle,
  Info,
  Search,
  Plus,
  Edit,
  Trash2,
  Send,
  Eye,
  MousePointer,
  ArrowRight,
  Home,
  Phone,
  Mail,
  MapPin,
  Clock,
  Save,
  Printer,
  X
} from 'lucide-react';

export default function AdminAnleitung() {
  const [openSections, setOpenSections] = useState(['start']);
  const [selectedGuide, setSelectedGuide] = useState(null);
  
  const toggleSection = (section) => {
    setOpenSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Visual step-by-step guides
  const StepByStep = ({ steps }) => (
    <div className="space-y-4 my-4">
      {steps.map((step, idx) => (
        <div key={idx} className="flex gap-4 items-start p-4 bg-gray-50 rounded-lg border-l-4 border-[#9B1B30]">
          <div className="w-8 h-8 rounded-full bg-[#9B1B30] text-white flex items-center justify-center font-bold flex-shrink-0">
            {idx + 1}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-900 mb-1">{step.title}</div>
            <div className="text-gray-600 text-sm">{step.description}</div>
            {step.visual && (
              <div className="mt-3 p-3 bg-white rounded border">
                {step.visual}
              </div>
            )}
            {step.tip && (
              <div className="mt-2 flex items-start gap-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{step.tip}</span>
              </div>
            )}
            {step.warning && (
              <div className="mt-2 flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-2 rounded">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{step.warning}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // Visual button representation
  const ButtonVisual = ({ icon: Icon, label, color = "bg-[#9B1B30]", className = "" }) => (
    <div className={`inline-flex items-center gap-2 px-3 py-2 ${color} text-white rounded-lg text-sm font-medium ${className}`}>
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </div>
  );

  // Visual icon button
  const IconButtonVisual = ({ icon: Icon, color = "text-gray-600", tip }) => (
    <div className="inline-flex flex-col items-center">
      <div className={`p-2 border rounded hover:bg-gray-50 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      {tip && <span className="text-xs text-gray-500 mt-1">{tip}</span>}
    </div>
  );

  // Visual form field
  const FormFieldVisual = ({ label, placeholder, type = "text" }) => (
    <div className="mb-2">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      <div className="mt-1 px-3 py-2 border rounded bg-gray-50 text-gray-400 text-sm">
        {placeholder}
      </div>
    </div>
  );

  // Color badge
  const ColorBadge = ({ color, label }) => (
    <div className="flex items-center gap-2">
      <div className={`w-4 h-4 rounded ${color}`}></div>
      <span className="text-sm">{label}</span>
    </div>
  );

  // Screenshot component with zoom functionality
  const Screenshot = ({ src, alt, caption }) => {
    const [isZoomed, setIsZoomed] = useState(false);
    
    return (
      <>
        <div className="my-4 border rounded-lg overflow-hidden shadow-md">
          <img 
            src={src} 
            alt={alt} 
            className="w-full cursor-zoom-in hover:opacity-90 transition-opacity"
            onClick={() => setIsZoomed(true)}
          />
          {caption && (
            <div className="bg-gray-100 px-4 py-2 text-sm text-gray-600 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              {caption}
            </div>
          )}
        </div>
        
        {/* Zoom Modal */}
        {isZoomed && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setIsZoomed(false)}
          >
            <button 
              className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70"
              onClick={() => setIsZoomed(false)}
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={src} 
              alt={alt} 
              className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
            />
          </div>
        )}
      </>
    );
  };

  const guides = {
    start: {
      title: "Erste Schritte",
      icon: Home,
      color: "bg-blue-500",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-[#9B1B30] to-[#7a1526] text-white p-6 rounded-xl">
            <h2 className="text-2xl font-bold mb-2">Willkommen bei FarbWelt Office!</h2>
            <p className="opacity-90">Diese Anleitung zeigt Ihnen Schritt für Schritt, wie Sie das System bedienen.</p>
          </div>

          {/* Login Screenshot */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold">🔐 So melden Sie sich an:</h3>
            <StepByStep steps={[
              {
                title: 'Öffnen Sie die FarbWelt-Seite',
                description: 'Klicken Sie auf "Anmelden" oben rechts',
                visual: <Screenshot src="/images/anleitung/00_homepage.jpeg" alt="Homepage" caption="Klicken Sie auf 'Anmelden'" />
              },
              {
                title: 'Geben Sie Ihre Zugangsdaten ein',
                description: 'Tragen Sie E-Mail und Passwort ein, dann klicken Sie auf "ANMELDEN"',
                visual: <Screenshot src="/images/anleitung/01b_login_filled.jpeg" alt="Login ausgefüllt" caption="Login-Formular" />
              },
              {
                title: 'Das Dashboard',
                description: 'Nach dem Login sehen Sie das Dashboard mit allen wichtigen Informationen',
                visual: <Screenshot src="/images/anleitung/02_dashboard.jpeg" alt="Dashboard" caption="Ihr Admin-Dashboard" />
              }
            ]} />
          </div>

          <h3 className="text-xl font-bold mt-8">📚 Wählen Sie einen Bereich:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-2 hover:border-[#9B1B30] transition-all cursor-pointer" onClick={() => setSelectedGuide('kunden')}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-500 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">Kunden verwalten</h3>
                  <p className="text-sm text-gray-500">Kunden anlegen, bearbeiten, suchen</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#9B1B30] transition-all cursor-pointer" onClick={() => setSelectedGuide('termine')}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-purple-500 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">Termine planen</h3>
                  <p className="text-sm text-gray-500">Kalender nutzen, Termine erstellen</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#9B1B30] transition-all cursor-pointer" onClick={() => setSelectedGuide('dokumente')}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-500 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">Dokumente erstellen</h3>
                  <p className="text-sm text-gray-500">Angebote, Rechnungen, AB</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#9B1B30] transition-all cursor-pointer" onClick={() => setSelectedGuide('protokolle')}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-teal-500 flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">Abnahmeprotokolle</h3>
                  <p className="text-sm text-gray-500">Protokolle mit Unterschrift</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },

    kunden: {
      title: "Kundenverwaltung",
      icon: Users,
      color: "bg-green-500",
      content: (
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={() => setSelectedGuide('start')}>
              ← Zurück
            </Button>
          </div>

          <h2 className="text-2xl font-bold text-[#9B1B30] flex items-center gap-2">
            <Users className="w-7 h-7" />
            Kundenverwaltung
          </h2>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h3 className="font-bold text-blue-800 mb-2">📍 Wo finde ich das?</h3>
              <p className="text-blue-700">Menü links → <strong>"Kunden"</strong></p>
            </CardContent>
          </Card>

          {/* Screenshot der Kundenseite */}
          <Screenshot src="/images/anleitung/03_kunden.jpeg" alt="Kundenverwaltung" caption="Übersicht der Kundenverwaltung" />

          <div className="space-y-8">
            {/* Neuen Kunden anlegen */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#9B1B30]" />
                Neuen Kunden anlegen
              </h3>
              
              {/* Screenshot des Neuer-Kunde-Dialogs */}
              <Screenshot src="/images/anleitung/07_neuer_kunde.jpeg" alt="Neuer Kunde Dialog" caption="Formular zum Anlegen eines neuen Kunden" />
              
              <StepByStep steps={[
                {
                  title: 'Klicken Sie auf den grünen Button',
                  description: 'Oben rechts auf der Kunden-Seite finden Sie diesen Button:',
                  visual: <ButtonVisual icon={Plus} label="Neuer Kunde" color="bg-green-600" />
                },
                {
                  title: 'Füllen Sie das Formular aus',
                  description: 'Geben Sie die Kundendaten ein:',
                  visual: (
                    <div className="grid grid-cols-2 gap-3">
                      <FormFieldVisual label="Vorname *" placeholder="z.B. Max" />
                      <FormFieldVisual label="Nachname *" placeholder="z.B. Mustermann" />
                      <FormFieldVisual label="E-Mail *" placeholder="max@beispiel.de" />
                      <FormFieldVisual label="Telefon *" placeholder="0123 456789" />
                      <div className="col-span-2">
                        <FormFieldVisual label="Straße" placeholder="Musterstraße 123" />
                      </div>
                      <FormFieldVisual label="PLZ" placeholder="40210" />
                      <FormFieldVisual label="Stadt" placeholder="Düsseldorf" />
                    </div>
                  ),
                  tip: 'Felder mit * sind Pflichtfelder!'
                },
                {
                  title: 'Speichern Sie den Kunden',
                  description: 'Klicken Sie auf den Speichern-Button:',
                  visual: <ButtonVisual icon={Save} label="Speichern" />,
                  tip: 'Die Kundennummer (z.B. FW-12345) wird automatisch vergeben!'
                }
              ]} />
            </div>

            {/* Kunden suchen */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-[#9B1B30]" />
                Kunden suchen
              </h3>
              
              <StepByStep steps={[
                {
                  title: 'Nutzen Sie das Suchfeld',
                  description: 'Oben auf der Seite gibt es ein Suchfeld. Tippen Sie einfach los:',
                  visual: (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <div className="pl-10 pr-4 py-2 border rounded-lg bg-white text-gray-400">
                        Suche nach Name, E-Mail, Telefon, Kundennummer...
                      </div>
                    </div>
                  ),
                  tip: 'Sie können nach Name, E-Mail, Telefonnummer, Kundennummer oder PLZ suchen!'
                }
              ]} />
            </div>

            {/* Kunden bearbeiten */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Edit className="w-5 h-5 text-[#9B1B30]" />
                Kunden bearbeiten / löschen
              </h3>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="font-medium mb-3">Bei jedem Kunden sehen Sie diese Buttons:</p>
                <div className="flex gap-4">
                  <IconButtonVisual icon={Edit} tip="Bearbeiten" />
                  <IconButtonVisual icon={Trash2} color="text-red-500" tip="Löschen" />
                  <IconButtonVisual icon={Calendar} color="text-purple-500" tip="Termin" />
                  <IconButtonVisual icon={FileText} color="text-orange-500" tip="Dokument" />
                </div>
              </div>
              
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-red-800">Achtung beim Löschen!</strong>
                    <p className="text-red-700 text-sm mt-1">Gelöschte Kunden können NICHT wiederhergestellt werden!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },

    termine: {
      title: "Terminverwaltung",
      icon: Calendar,
      color: "bg-purple-500",
      content: (
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={() => setSelectedGuide('start')}>
              ← Zurück
            </Button>
          </div>

          <h2 className="text-2xl font-bold text-[#9B1B30] flex items-center gap-2">
            <Calendar className="w-7 h-7" />
            Terminverwaltung / Kalender
          </h2>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h3 className="font-bold text-blue-800 mb-2">📍 Wo finde ich das?</h3>
              <p className="text-blue-700">Menü links → <strong>"Termine"</strong></p>
            </CardContent>
          </Card>

          {/* Screenshot des Kalenders */}
          <Screenshot src="/images/anleitung/04_termine.jpeg" alt="Terminkalender" caption="Der Wochenkalender zeigt alle Termine auf einen Blick. Rote Tage = keine Arbeit erlaubt!" />

          <div className="space-y-8">
            {/* Kalender-Ansichten */}
            <div>
              <h3 className="text-xl font-bold mb-4">📅 Kalender-Ansichten</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="text-center p-4">
                  <div className="font-bold mb-2">Liste</div>
                  <p className="text-xs text-gray-500">Alle kommenden Termine als Liste</p>
                </Card>
                <Card className="text-center p-4 border-[#9B1B30] border-2">
                  <div className="font-bold mb-2 text-[#9B1B30]">Woche ⭐</div>
                  <p className="text-xs text-gray-500">7-Tage-Ansicht mit Zeitachse</p>
                </Card>
                <Card className="text-center p-4">
                  <div className="font-bold mb-2">Monat</div>
                  <p className="text-xs text-gray-500">Ganzer Monat im Überblick</p>
                </Card>
                <Card className="text-center p-4">
                  <div className="font-bold mb-2">Tag</div>
                  <p className="text-xs text-gray-500">Ein Tag im Detail</p>
                </Card>
              </div>
            </div>

            {/* Termin erstellen */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#9B1B30]" />
                Neuen Termin erstellen
              </h3>
              
              {/* Screenshot des Termin-Dialogs */}
              <Screenshot src="/images/anleitung/08_neuer_termin.jpeg" alt="Neuer Termin Dialog" caption="Formular zum Erstellen eines neuen Termins" />
              
              <StepByStep steps={[
                {
                  title: 'Klicken Sie auf "+ Termin anlegen"',
                  description: 'Oder klicken Sie direkt auf ein leeres Zeitfeld im Kalender',
                  visual: <ButtonVisual icon={Plus} label="Termin anlegen" />
                },
                {
                  title: 'Wählen Sie die Terminart',
                  description: 'Jede Art hat eine eigene Farbe:',
                  visual: (
                    <div className="grid grid-cols-2 gap-3">
                      <ColorBadge color="bg-orange-500" label="🏠 Besichtigung" />
                      <ColorBadge color="bg-green-500" label="🔨 Baustelle" />
                      <ColorBadge color="bg-red-500" label="📞 Rückruf" />
                      <ColorBadge color="bg-gray-500" label="🔒 Privat" />
                    </div>
                  )
                },
                {
                  title: 'Wählen Sie einen Kunden',
                  description: 'Tippen Sie den Namen ein - Kunden werden automatisch vorgeschlagen:',
                  visual: (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-2 border-b bg-gray-50">
                        <Search className="inline w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-gray-400">Max...</span>
                      </div>
                      <div className="p-2 bg-white border-b hover:bg-blue-50 cursor-pointer">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-[#9B1B30]/10 rounded-full flex items-center justify-center">
                            <Users className="w-4 h-4 text-[#9B1B30]" />
                          </div>
                          <div>
                            <div className="font-medium">Max Mustermann</div>
                            <div className="text-xs text-gray-500">FW-12345</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  title: 'Wählen Sie Datum und Uhrzeit',
                  description: 'Klicken Sie auf das Datum-Feld und die Uhrzeit',
                  visual: (
                    <div className="grid grid-cols-2 gap-3">
                      <FormFieldVisual label="Datum" placeholder="15.02.2026" />
                      <FormFieldVisual label="Uhrzeit" placeholder="10:00" />
                    </div>
                  )
                },
                {
                  title: 'Speichern',
                  description: 'Klicken Sie auf "Termin erstellen"',
                  visual: <ButtonVisual label="Termin erstellen" />,
                  tip: 'Der Kunde erhält automatisch eine E-Mail mit den Termindetails!'
                }
              ]} />
            </div>

            {/* Farbcodierung */}
            <div>
              <h3 className="text-xl font-bold mb-4">🎨 Was bedeuten die Farben?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <h4 className="font-bold mb-3">Terminarten:</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-2 bg-orange-100 rounded">
                      <div className="w-6 h-6 bg-orange-500 rounded"></div>
                      <span><strong>Orange</strong> = Besichtigung</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-green-100 rounded">
                      <div className="w-6 h-6 bg-green-500 rounded"></div>
                      <span><strong>Grün</strong> = Baustelle / Arbeitsbeginn</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-red-100 rounded">
                      <div className="w-6 h-6 bg-red-500 rounded"></div>
                      <span><strong>Rot</strong> = Rückruf</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-gray-100 rounded">
                      <div className="w-6 h-6 bg-gray-500 rounded"></div>
                      <span><strong>Grau</strong> = Privater Termin</span>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 border-red-300 bg-red-50">
                  <h4 className="font-bold mb-3 text-red-800">🚫 Arbeitsfreie Tage (Hellrot):</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>❌ Jeden <strong>Sonntag</strong></li>
                    <li>❌ <strong>Feiertage</strong> (Ostern, Pfingsten, Weihnachten...)</li>
                    <li>❌ <strong>Karneval</strong> (Rosenmontag, Weiberfastnacht...)</li>
                    <li>❌ <strong>NRW-Feiertage</strong> (Fronleichnam, Allerheiligen)</li>
                  </ul>
                  <div className="mt-3 p-2 bg-red-100 rounded text-red-800 text-sm">
                    ⚠️ An diesen Tagen keine Baustellen-Termine!
                  </div>
                </Card>
              </div>
            </div>

            {/* Navigation */}
            <div>
              <h3 className="text-xl font-bold mb-4">🔄 Navigation im Kalender</h3>
              <div className="bg-gray-900 text-white p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" className="text-white">
                    <ChevronDown className="w-5 h-5 rotate-90" />
                  </Button>
                  <span className="font-bold">Februar 2026</span>
                  <Button variant="ghost" size="sm" className="text-white">
                    <ChevronDown className="w-5 h-5 -rotate-90" />
                  </Button>
                </div>
                <p className="text-center text-sm text-gray-400 mt-2">← Vorherige Woche | Nächste Woche →</p>
              </div>
            </div>
          </div>
        </div>
      )
    },

    dokumente: {
      title: "FarbWelt Office",
      icon: FileText,
      color: "bg-orange-500",
      content: (
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={() => setSelectedGuide('start')}>
              ← Zurück
            </Button>
          </div>

          <h2 className="text-2xl font-bold text-[#9B1B30] flex items-center gap-2">
            <FileText className="w-7 h-7" />
            FarbWelt Office - Dokumente erstellen
          </h2>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h3 className="font-bold text-blue-800 mb-2">📍 Wo finde ich das?</h3>
              <p className="text-blue-700">Menü links → <strong>"FarbWelt Office"</strong></p>
            </CardContent>
          </Card>

          {/* Screenshot von FarbWelt Office */}
          <Screenshot src="/images/anleitung/05_dokumente.jpeg" alt="FarbWelt Office" caption="Hier erstellen Sie Angebote, Rechnungen und Auftragsbestätigungen" />

          <div className="space-y-8">
            {/* Dokumenttypen */}
            <div>
              <h3 className="text-xl font-bold mb-4">📄 Welche Dokumente kann ich erstellen?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 border-l-4 border-blue-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-bold">AN</span>
                    <h4 className="font-bold">Angebot</h4>
                  </div>
                  <p className="text-sm text-gray-600">Kostenvoranschlag für den Kunden, BEVOR die Arbeit beginnt</p>
                </Card>
                <Card className="p-4 border-l-4 border-green-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">RE</span>
                    <h4 className="font-bold">Rechnung</h4>
                  </div>
                  <p className="text-sm text-gray-600">Abrechnung NACH Fertigstellung der Arbeit</p>
                </Card>
                <Card className="p-4 border-l-4 border-orange-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm font-bold">AB</span>
                    <h4 className="font-bold">Auftragsbestätigung</h4>
                  </div>
                  <p className="text-sm text-gray-600">Bestätigung, dass der Auftrag angenommen wurde</p>
                </Card>
              </div>
            </div>

            {/* Archiv Screenshot */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-[#9B1B30]" />
                Das Archiv
              </h3>
              <p className="text-gray-600 mb-4">Im Archiv finden Sie alle erstellten Dokumente. Sie können diese herunterladen, bearbeiten oder per E-Mail versenden.</p>
              <Screenshot src="/images/anleitung/09_archiv.jpeg" alt="Dokumenten-Archiv" caption="Alle gespeicherten Dokumente auf einen Blick" />
            </div>

            {/* Dokument erstellen */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#9B1B30]" />
                Neues Dokument erstellen
              </h3>
              
              <StepByStep steps={[
                {
                  title: 'Wählen Sie den Dokumenttyp',
                  description: 'Klicken Sie auf Angebot, Rechnung oder AB:',
                  visual: (
                    <div className="flex gap-2">
                      <div className="px-4 py-2 bg-[#9B1B30] text-white rounded-lg">Angebot</div>
                      <div className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">Rechnung</div>
                      <div className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">AB</div>
                    </div>
                  )
                },
                {
                  title: 'Wählen Sie den Kunden',
                  description: 'Tippen Sie den Namen ein und klicken Sie auf den Kunden:',
                  visual: (
                    <div className="border rounded-lg p-3 bg-green-50 border-green-300">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="font-bold text-green-800">Max Mustermann</div>
                          <div className="text-sm text-green-600">FW-12345 | Düsseldorf</div>
                        </div>
                        <CheckCircle className="w-6 h-6 text-green-500 ml-auto" />
                      </div>
                    </div>
                  ),
                  tip: 'Grün = Kunde ausgewählt!'
                },
                {
                  title: 'Fügen Sie Positionen hinzu',
                  description: 'Das sind die Arbeiten/Leistungen die abgerechnet werden:',
                  visual: (
                    <div className="border rounded overflow-hidden">
                      <div className="grid grid-cols-5 gap-2 p-2 bg-gray-100 text-sm font-medium">
                        <div>Pos</div>
                        <div className="col-span-2">Beschreibung</div>
                        <div>Preis</div>
                        <div>Menge</div>
                      </div>
                      <div className="grid grid-cols-5 gap-2 p-2 border-t text-sm">
                        <div className="bg-gray-50 p-1 rounded">1</div>
                        <div className="col-span-2 bg-gray-50 p-1 rounded text-gray-400">Malerarbeiten Wohnzimmer...</div>
                        <div className="bg-gray-50 p-1 rounded text-gray-400">450,00</div>
                        <div className="bg-gray-50 p-1 rounded text-gray-400">1</div>
                      </div>
                    </div>
                  ),
                  tip: 'Drücken Sie ENTER in der Beschreibung für eine neue Zeile!'
                },
                {
                  title: 'Neue Position hinzufügen',
                  description: 'Klicken Sie auf diesen Button für weitere Positionen:',
                  visual: <ButtonVisual icon={Plus} label="Position hinzufügen" color="bg-gray-600" />
                },
                {
                  title: 'PDF erstellen',
                  description: 'Wenn alles fertig ist, klicken Sie auf:',
                  visual: <ButtonVisual icon={FileText} label="PDF generieren" />,
                  tip: 'Das PDF wird automatisch heruntergeladen!'
                }
              ]} />
            </div>

            {/* Archiv */}
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-[#9B1B30]" />
                Dokumente im Archiv bearbeiten
              </h3>
              
              <Card className="bg-yellow-50 border-yellow-200 p-4 mb-4">
                <h4 className="font-bold text-yellow-800 mb-2">📍 So finden Sie das Archiv:</h4>
                <p className="text-yellow-700">Klicken Sie oben auf den Tab <strong>"Archiv"</strong></p>
              </Card>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="font-medium mb-3">Diese Buttons sehen Sie bei jedem Dokument:</p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex flex-col items-center">
                    <div className="p-2 border rounded bg-white text-blue-600">
                      <ArrowRight className="w-5 h-5" />
                    </div>
                    <span className="text-xs mt-1 text-center">Weiterführen<br/>(Angebot → RE/AB)</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="p-2 border rounded bg-white">
                      <Edit className="w-5 h-5" />
                    </div>
                    <span className="text-xs mt-1">Bearbeiten</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="p-2 border rounded bg-white">
                      <Download className="w-5 h-5" />
                    </div>
                    <span className="text-xs mt-1">PDF laden</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="p-2 border rounded bg-white">
                      <Send className="w-5 h-5" />
                    </div>
                    <span className="text-xs mt-1">E-Mail</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="p-2 border rounded bg-white text-red-500">
                      <Trash2 className="w-5 h-5" />
                    </div>
                    <span className="text-xs mt-1">Löschen</span>
                  </div>
                </div>
              </div>

              <StepByStep steps={[
                {
                  title: 'Angebot → Rechnung umwandeln',
                  description: 'Klicken Sie auf den blauen Pfeil → "Als Rechnung"',
                  visual: (
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-bold">AN-2026-001</span>
                      <ArrowRight className="w-5 h-5 text-blue-600" />
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">RE-2026-001</span>
                    </div>
                  ),
                  tip: 'Alle Positionen werden automatisch übernommen!'
                }
              ]} />
            </div>
          </div>
        </div>
      )
    },

    protokolle: {
      title: "Abnahmeprotokolle",
      icon: ClipboardCheck,
      color: "bg-teal-500",
      content: (
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={() => setSelectedGuide('start')}>
              ← Zurück
            </Button>
          </div>

          <h2 className="text-2xl font-bold text-[#9B1B30] flex items-center gap-2">
            <ClipboardCheck className="w-7 h-7" />
            Abnahmeprotokoll erstellen
          </h2>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h3 className="font-bold text-blue-800 mb-2">📍 Wo finde ich das?</h3>
              <p className="text-blue-700">Menü links → <strong>"Abnahmeprotokoll"</strong></p>
            </CardContent>
          </Card>

          {/* Screenshot des Protokoll-Bereichs */}
          <Screenshot src="/images/anleitung/06_protokoll.jpeg" alt="Abnahmeprotokoll" caption="Hier erstellen Sie Abnahmeprotokolle mit digitaler Unterschrift" />

          <StepByStep steps={[
            {
              title: 'Klicken Sie auf "Neues Protokoll"',
              description: 'Oben auf der Seite finden Sie den Button:',
              visual: <ButtonVisual icon={Plus} label="Neues Protokoll" />
            },
            {
              title: 'Wählen Sie den Kunden',
              description: 'Suchen Sie den Kunden in der Liste',
            },
            {
              title: 'Beschreiben Sie die durchgeführten Arbeiten',
              description: 'Schreiben Sie detailliert, was gemacht wurde:',
              visual: (
                <div className="border rounded p-3 bg-gray-50">
                  <div className="text-xs text-gray-500 mb-1">Arbeitsbeschreibung</div>
                  <div className="text-sm text-gray-700">
                    - Wände im Wohnzimmer gestrichen (2 Anstriche)<br/>
                    - Decke weiß gestrichen<br/>
                    - Fußleisten lackiert
                  </div>
                </div>
              )
            },
            {
              title: 'Fügen Sie Fotos hinzu (optional)',
              description: 'Vorher/Nachher-Bilder als Nachweis',
              visual: <ButtonVisual icon={Plus} label="Fotos hinzufügen" color="bg-gray-600" />
            },
            {
              title: 'Kundenunterschrift einholen',
              description: 'Der Kunde unterschreibt direkt auf dem Bildschirm:',
              visual: (
                <div className="border-2 border-dashed rounded-lg p-8 text-center bg-gray-50">
                  <Edit className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-gray-500">Hier unterschreiben</span>
                </div>
              ),
              tip: 'Funktioniert auch mit dem Finger auf Tablet/Handy!'
            },
            {
              title: 'Protokoll erstellen',
              description: 'Klicken Sie auf den Button:',
              visual: <ButtonVisual label="Protokoll erstellen" />,
              tip: 'Das PDF kann direkt per E-Mail an den Kunden gesendet werden!'
            }
          ]} />
        </div>
      )
    }
  };

  const downloadPDF = () => {
    // Create printable HTML
    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>FarbWelt Office - Benutzerhandbuch</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
    h1 { color: #9B1B30; border-bottom: 3px solid #9B1B30; padding-bottom: 10px; }
    h2 { color: #9B1B30; margin-top: 40px; }
    h3 { color: #333; margin-top: 20px; }
    .step { display: flex; gap: 15px; margin: 15px 0; padding: 15px; background: #f5f5f5; border-left: 4px solid #9B1B30; border-radius: 0 8px 8px 0; }
    .step-number { width: 30px; height: 30px; background: #9B1B30; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; flex-shrink: 0; }
    .step-content { flex: 1; }
    .step-title { font-weight: bold; margin-bottom: 5px; }
    .tip { background: #e3f2fd; padding: 10px; border-radius: 5px; margin-top: 10px; font-size: 14px; }
    .warning { background: #fff3e0; padding: 10px; border-radius: 5px; margin-top: 10px; font-size: 14px; }
    .color-box { display: inline-block; width: 20px; height: 20px; border-radius: 4px; vertical-align: middle; margin-right: 8px; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
    th { background: #f5f5f5; }
    @media print { h2 { page-break-before: always; } }
  </style>
</head>
<body>
  <h1>🏠 FarbWelt Office - Benutzerhandbuch</h1>
  <p><strong>Version:</strong> 2.0 | <strong>Stand:</strong> ${new Date().toLocaleDateString('de-DE')}</p>
  <p>Dieses Handbuch erklärt Schritt für Schritt alle Funktionen des Systems.</p>

  <h2>👥 1. Kundenverwaltung</h2>
  <p><strong>Wo?</strong> Menü links → "Kunden"</p>
  
  <h3>Neuen Kunden anlegen:</h3>
  <div class="step">
    <div class="step-number">1</div>
    <div class="step-content">
      <div class="step-title">Klicken Sie auf "+ Neuer Kunde"</div>
      Dieser grüne Button ist oben rechts.
    </div>
  </div>
  <div class="step">
    <div class="step-number">2</div>
    <div class="step-content">
      <div class="step-title">Füllen Sie das Formular aus</div>
      Pflichtfelder: Vorname, Nachname, E-Mail, Telefon
      <div class="tip">💡 Die Kundennummer (z.B. FW-12345) wird automatisch vergeben!</div>
    </div>
  </div>
  <div class="step">
    <div class="step-number">3</div>
    <div class="step-content">
      <div class="step-title">Klicken Sie auf "Speichern"</div>
    </div>
  </div>

  <h3>Kunden suchen:</h3>
  <p>Nutzen Sie das Suchfeld oben. Sie können suchen nach: Name, E-Mail, Telefon, Kundennummer, PLZ</p>

  <h3>Aktions-Buttons bei jedem Kunden:</h3>
  <table>
    <tr><th>Symbol</th><th>Bedeutung</th></tr>
    <tr><td>✏️</td><td>Kunden bearbeiten</td></tr>
    <tr><td>🗑️</td><td>Kunden löschen</td></tr>
    <tr><td>📅</td><td>Termin erstellen</td></tr>
    <tr><td>📄</td><td>Dokument erstellen</td></tr>
  </table>
  <div class="warning">⚠️ Achtung: Gelöschte Kunden können NICHT wiederhergestellt werden!</div>

  <h2>📅 2. Terminverwaltung / Kalender</h2>
  <p><strong>Wo?</strong> Menü links → "Termine"</p>

  <h3>Kalender-Ansichten:</h3>
  <table>
    <tr><th>Ansicht</th><th>Beschreibung</th></tr>
    <tr><td>Liste</td><td>Alle kommenden Termine als Liste</td></tr>
    <tr><td><strong>Woche ⭐</strong></td><td>7-Tage-Ansicht mit Zeitachse (empfohlen)</td></tr>
    <tr><td>Monat</td><td>Ganzer Monat im Überblick</td></tr>
    <tr><td>Tag</td><td>Ein Tag im Detail</td></tr>
  </table>

  <h3>Neuen Termin erstellen:</h3>
  <div class="step">
    <div class="step-number">1</div>
    <div class="step-content">
      <div class="step-title">Klicken Sie auf "+ Termin anlegen"</div>
      Oder klicken Sie direkt auf ein leeres Zeitfeld im Kalender.
    </div>
  </div>
  <div class="step">
    <div class="step-number">2</div>
    <div class="step-content">
      <div class="step-title">Wählen Sie die Terminart</div>
      <span class="color-box" style="background:#f97316"></span><strong>Orange</strong> = Besichtigung<br>
      <span class="color-box" style="background:#22c55e"></span><strong>Grün</strong> = Baustelle/Arbeitsbeginn<br>
      <span class="color-box" style="background:#ef4444"></span><strong>Rot</strong> = Rückruf<br>
      <span class="color-box" style="background:#6b7280"></span><strong>Grau</strong> = Privater Termin
    </div>
  </div>
  <div class="step">
    <div class="step-number">3</div>
    <div class="step-content">
      <div class="step-title">Wählen Sie einen Kunden</div>
      Tippen Sie den Namen ein - Kunden werden automatisch vorgeschlagen.
    </div>
  </div>
  <div class="step">
    <div class="step-number">4</div>
    <div class="step-content">
      <div class="step-title">Wählen Sie Datum und Uhrzeit</div>
    </div>
  </div>
  <div class="step">
    <div class="step-number">5</div>
    <div class="step-content">
      <div class="step-title">Klicken Sie auf "Termin erstellen"</div>
      <div class="tip">💡 Der Kunde erhält automatisch eine E-Mail!</div>
    </div>
  </div>

  <h3>🚫 Arbeitsfreie Tage (hellrot markiert):</h3>
  <p>An diesen Tagen sind keine Baustellentermine erlaubt:</p>
  <ul>
    <li>Jeden <strong>Sonntag</strong> (Sonntagsruhe)</li>
    <li><strong>Feiertage</strong> (Neujahr, Ostern, Pfingsten, Weihnachten...)</li>
    <li><strong>Karneval</strong> (Weiberfastnacht, Rosenmontag, Karnevalsdienstag)</li>
    <li><strong>NRW-Feiertage</strong> (Fronleichnam, Allerheiligen)</li>
  </ul>

  <h2>📄 3. FarbWelt Office (Dokumente)</h2>
  <p><strong>Wo?</strong> Menü links → "FarbWelt Office"</p>

  <h3>Dokumenttypen:</h3>
  <table>
    <tr><th>Typ</th><th>Kürzel</th><th>Verwendung</th></tr>
    <tr><td>Angebot</td><td>AN</td><td>Kostenvoranschlag VOR der Arbeit</td></tr>
    <tr><td>Rechnung</td><td>RE</td><td>Abrechnung NACH der Arbeit</td></tr>
    <tr><td>Auftragsbestätigung</td><td>AB</td><td>Bestätigung des Auftrags</td></tr>
  </table>

  <h3>Neues Dokument erstellen:</h3>
  <div class="step">
    <div class="step-number">1</div>
    <div class="step-content">
      <div class="step-title">Dokumenttyp wählen</div>
      Klicken Sie auf Angebot, Rechnung oder AB.
    </div>
  </div>
  <div class="step">
    <div class="step-number">2</div>
    <div class="step-content">
      <div class="step-title">Kunden wählen</div>
      Tippen Sie den Namen ein und wählen Sie den Kunden aus.
    </div>
  </div>
  <div class="step">
    <div class="step-number">3</div>
    <div class="step-content">
      <div class="step-title">Positionen eingeben</div>
      <table>
        <tr><th>Feld</th><th>Erklärung</th></tr>
        <tr><td>Pos</td><td>Positionsnummer (1, 2, 3...)</td></tr>
        <tr><td>Beschreibung</td><td>Was wurde gemacht?</td></tr>
        <tr><td>Preis</td><td>Einzelpreis in EUR</td></tr>
        <tr><td>Menge</td><td>Anzahl</td></tr>
      </table>
      <div class="tip">💡 Drücken Sie ENTER für eine neue Zeile in der Beschreibung!</div>
    </div>
  </div>
  <div class="step">
    <div class="step-number">4</div>
    <div class="step-content">
      <div class="step-title">Klicken Sie auf "PDF generieren"</div>
      Das PDF wird automatisch heruntergeladen.
    </div>
  </div>

  <h3>Archiv-Funktionen:</h3>
  <table>
    <tr><th>Button</th><th>Funktion</th></tr>
    <tr><td>→</td><td>Angebot in Rechnung/AB umwandeln</td></tr>
    <tr><td>✏️</td><td>Dokument bearbeiten</td></tr>
    <tr><td>⬇️</td><td>PDF herunterladen</td></tr>
    <tr><td>✉️</td><td>Per E-Mail senden</td></tr>
    <tr><td>🗑️</td><td>Löschen</td></tr>
  </table>

  <h2>📋 4. Abnahmeprotokoll</h2>
  <p><strong>Wo?</strong> Menü links → "Abnahmeprotokoll"</p>

  <h3>Neues Protokoll erstellen:</h3>
  <div class="step">
    <div class="step-number">1</div>
    <div class="step-content">
      <div class="step-title">Klicken Sie auf "+ Neues Protokoll"</div>
    </div>
  </div>
  <div class="step">
    <div class="step-number">2</div>
    <div class="step-content">
      <div class="step-title">Wählen Sie den Kunden</div>
    </div>
  </div>
  <div class="step">
    <div class="step-number">3</div>
    <div class="step-content">
      <div class="step-title">Beschreiben Sie die Arbeiten</div>
      Schreiben Sie detailliert, was gemacht wurde.
    </div>
  </div>
  <div class="step">
    <div class="step-number">4</div>
    <div class="step-content">
      <div class="step-title">Fotos hinzufügen (optional)</div>
      Vorher/Nachher-Bilder als Nachweis.
    </div>
  </div>
  <div class="step">
    <div class="step-number">5</div>
    <div class="step-content">
      <div class="step-title">Kundenunterschrift einholen</div>
      Der Kunde unterschreibt direkt auf dem Bildschirm.
      <div class="tip">💡 Funktioniert auch mit dem Finger auf Tablet/Handy!</div>
    </div>
  </div>
  <div class="step">
    <div class="step-number">6</div>
    <div class="step-content">
      <div class="step-title">Klicken Sie auf "Protokoll erstellen"</div>
    </div>
  </div>

  <hr style="margin-top: 50px;">
  <p style="text-align: center; color: #666;">
    <strong>FarbWelt Office</strong> | Benutzerhandbuch v2.0<br>
    Bei Fragen wenden Sie sich an Ihren Administrator.
  </p>
</body>
</html>
    `;
    
    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    printWindow.onload = () => printWindow.print();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-[#9B1B30]" />
            Benutzerhandbuch
          </h1>
          <p className="text-gray-500 mt-1">Schritt-für-Schritt Anleitung für alle Mitarbeiter</p>
        </div>
        <Button onClick={downloadPDF} className="bg-[#9B1B30] hover:bg-[#7a1526]">
          <Printer className="w-4 h-4 mr-2" />
          Anleitung drucken / PDF
        </Button>
      </div>

      {/* Main Content */}
      <Card>
        <CardContent className="p-6">
          {selectedGuide ? (
            guides[selectedGuide]?.content
          ) : (
            guides.start.content
          )}
        </CardContent>
      </Card>
    </div>
  );
}
